import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Sparkle, Plus, Trash, FloppyDisk, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUSES = ["draft", "sent", "viewed", "accepted", "rejected"];

const empty = {
  title: "",
  client_name: "",
  client_company: "",
  client_email: "",
  introduction: "",
  requirements: "",
  scope: "",
  terms: "Payment: 50% advance, 50% on delivery.\nDelivery: 2-3 weeks from PO.\nValidity: 30 days from issue.",
  items: [],
  tax_percent: 18,
  discount: 0,
  status: "draft",
  lead_id: null,
};

export default function ProposalBuilder() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const presetLeadId = searchParams.get("lead");
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [proposalId, setProposalId] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/leads").then((r) => {
      setLeads(r.data);
      // Auto-prefill from ?lead=<id> when creating a new proposal
      if (isNew && presetLeadId) {
        const lead = r.data.find((l) => l.id === presetLeadId);
        if (lead) {
          const leadItems = (lead.selected_products || []).map((p) => ({
            name: p.name,
            description: p.description || "",
            quantity: Number(p.quantity || 1),
            unit_price: Number(p.unit_price || 0),
            unit: p.unit || "Unit",
            brochure_path: p.brochure_path || null,
            brochure_filename: p.brochure_filename || null,
          }));
          const remarkLines = (lead.remarks_log || []).slice(-3).map((rmk) => `• ${rmk.text}`).join("\n");
          setForm((f) => ({
            ...f,
            lead_id: lead.id,
            client_name: lead.name,
            client_company: lead.company || "",
            client_email: lead.email || "",
            requirements: lead.requirement || f.requirements,
            title: f.title || (lead.company ? `Proposal for ${lead.company}` : `Proposal for ${lead.name}`),
            items: leadItems,
            introduction: remarkLines ? `Recent context from lead:\n${remarkLines}` : f.introduction,
          }));
          if (leadItems.length > 0) {
            toast.success(`Pulled ${leadItems.length} product${leadItems.length > 1 ? "s" : ""} from lead`);
          }
        }
      }
    }).catch(() => {});
    if (!isNew) {
      api.get(`/proposals/${id}`).then((r) => {
        setForm({ ...empty, ...r.data });
        setProposalId(r.data.id);
        setPublicToken(r.data.public_token);
      });
    }
  }, [id, isNew, presetLeadId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addItem = (product) => {
    const uid = `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    if (product) {
      setForm((f) => ({ ...f, items: [...f.items, { _uid: uid, name: product.name, description: product.description, quantity: 1, unit_price: product.price, unit: product.unit, brochure_path: product.brochure_path || null, brochure_filename: product.brochure_filename || null }] }));
    } else {
      setForm((f) => ({ ...f, items: [...f.items, { _uid: uid, name: "", description: "", quantity: 1, unit_price: 0, unit: "Unit", brochure_path: null, brochure_filename: null }] }));
    }
  };

  const updateItem = (idx, k, v) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  };

  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const aiGenerate = async () => {
    if (!form.client_name || !form.requirements) {
      toast.error("Add client name and requirements first");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/proposals/generate", {
        client_name: form.client_name,
        client_company: form.client_company,
        requirements: form.requirements,
        items: form.items,
      });
      setForm((f) => ({
        ...f,
        introduction: data.introduction || f.introduction,
        scope: data.scope || f.scope,
        terms: data.terms || f.terms,
      }));
      toast.success("AI proposal drafted");
    } catch (e) {
      toast.error("AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!form.title || !form.client_name) { toast.error("Title and client name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tax_percent: parseFloat(form.tax_percent || 0),
        discount: parseFloat(form.discount || 0),
        items: form.items.map((i) => ({ ...i, quantity: parseFloat(i.quantity || 0), unit_price: parseFloat(i.unit_price || 0) })),
      };
      if (proposalId) {
        const { data } = await api.put(`/proposals/${proposalId}`, payload);
        setForm({ ...empty, ...data });
        toast.success("Saved");
      } else {
        const { data } = await api.post("/proposals", payload);
        setProposalId(data.id);
        setPublicToken(data.public_token);
        toast.success("Created");
        navigate(`/proposals/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity || 0) * Number(i.unit_price || 0)), 0);
  const afterDisc = subtotal - Number(form.discount || 0);
  const tax = afterDisc * (Number(form.tax_percent || 0) / 100);
  const total = afterDisc + tax;

  const copyLink = () => {
    if (!publicToken) return;
    const url = `${window.location.origin}/p/${publicToken}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied");
  };

  return (
    <Layout
      title={isNew ? "New Proposal" : "Edit Proposal"}
      subtitle="AI-drafted, Shelvestech-branded"
      actions={
        <>
          {publicToken && (
            <>
              <button onClick={copyLink} data-testid="copy-public-link" className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition">
                <ArrowSquareOut size={16} weight="bold" /> Copy Link
              </button>
              <a href={`${API}/proposals/${proposalId}/pdf`} target="_blank" rel="noreferrer" data-testid="download-pdf" className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition">
                <DownloadSimple size={16} weight="bold" /> PDF
              </a>
            </>
          )}
          <button onClick={save} disabled={saving} data-testid="save-proposal-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95 disabled:opacity-50">
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: form */}
        <div className="lg:col-span-7 space-y-6">
          <div className="border border-black/10 bg-white p-6">
            <div className="overline mb-4">Proposal details</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Title *</Label><Input data-testid="title-input" value={form.title} onChange={(e) => setField("title", e.target.value)} className="rounded-none" /></div>
              <div>
                <Label>Linked Lead</Label>
                <Select value={form.lead_id || "none"} onValueChange={(v) => {
                  if (v === "none") { setField("lead_id", null); return; }
                  const lead = leads.find((l) => l.id === v);
                  if (!lead) return;
                  const leadItems = (lead.selected_products || []).map((p) => ({
                    name: p.name,
                    description: p.description || "",
                    quantity: Number(p.quantity || 1),
                    unit_price: Number(p.unit_price || 0),
                    unit: p.unit || "Unit",
                    brochure_path: p.brochure_path || null,
                    brochure_filename: p.brochure_filename || null,
                  }));
                  // Build a quick lookup of existing items by name so we don't duplicate
                  const existingNames = new Set((form.items || []).map((it) => (it.name || "").trim().toLowerCase()));
                  const newOnes = leadItems.filter((it) => !existingNames.has((it.name || "").trim().toLowerCase()));
                  const remarkLines = (lead.remarks_log || []).slice(-3).map((r) => `• ${r.text}`).join("\n");
                  setForm((f) => ({
                    ...f,
                    lead_id: lead.id,
                    client_name: lead.name,
                    client_company: lead.company || "",
                    client_email: lead.email || "",
                    requirements: lead.requirement || f.requirements,
                    items: [...(f.items || []), ...newOnes],
                  }));
                  if (newOnes.length > 0) {
                    toast.success(`Pulled ${newOnes.length} product${newOnes.length > 1 ? "s" : ""} from lead`);
                  }
                  if (remarkLines && !form.introduction) {
                    // Surface recent remarks as a hint in the introduction text
                    setForm((f) => ({ ...f, introduction: `Recent context from lead:\n${remarkLines}` }));
                  }
                }}>
                  <SelectTrigger className="rounded-none" data-testid="linked-lead-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} {l.company && `(${l.company})`} {(l.selected_products?.length || 0) > 0 ? `· ${l.selected_products.length} prod` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Client Name *</Label><Input value={form.client_name} onChange={(e) => setField("client_name", e.target.value)} className="rounded-none" /></div>
              <div><Label>Client Company</Label><Input value={form.client_company} onChange={(e) => setField("client_company", e.target.value)} className="rounded-none" /></div>
              <div className="col-span-2"><Label>Client Email</Label><Input value={form.client_email} onChange={(e) => setField("client_email", e.target.value)} className="rounded-none" /></div>
              <div className="col-span-2"><Label>Client Requirements</Label><Textarea value={form.requirements} onChange={(e) => setField("requirements", e.target.value)} rows={3} className="rounded-none" placeholder="Briefly describe what the client needs…" /></div>
            </div>
            <button onClick={aiGenerate} disabled={generating} data-testid="ai-generate-button" className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#D90429] active:scale-95 disabled:opacity-50 transition-all">
              <Sparkle size={16} weight="fill" /> {generating ? "Generating with Gemini…" : "AI Draft (Gemini 3 Flash)"}
            </button>
          </div>

          <div className="border border-black/10 bg-white p-6">
            <div className="overline mb-4">Content</div>
            <Label>Introduction</Label>
            <Textarea data-testid="intro-input" value={form.introduction} onChange={(e) => setField("introduction", e.target.value)} rows={4} className="rounded-none mb-4" />
            <Label>Scope of Work</Label>
            <Textarea value={form.scope} onChange={(e) => setField("scope", e.target.value)} rows={5} className="rounded-none mb-4" />
            <Label>Terms & Conditions</Label>
            <Textarea value={form.terms} onChange={(e) => setField("terms", e.target.value)} rows={4} className="rounded-none" />
          </div>

          <div className="border border-black/10 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Commercials</div>
              <div className="flex gap-2">
                <Select onValueChange={(v) => { const p = products.find((x) => x.id === v); if (p) addItem(p); }}>
                  <SelectTrigger className="w-44 h-9 rounded-none text-sm" data-testid="add-from-catalog"><SelectValue placeholder="From catalog…" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => addItem(null)} data-testid="add-item-button" className="inline-flex items-center gap-1 px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black/5"><Plus size={14} /> Item</button>
              </div>
            </div>
            <div className="space-y-2">
              {form.items.length === 0 && <div className="text-sm text-[#717680] py-6 text-center border border-dashed border-black/15">No items yet. Add from catalog or create custom.</div>}
              {form.items.map((it, idx) => {
                const includeBrochure = it.include_brochure !== false; // default ON
                return (
                <div key={it._uid || `legacy_${idx}`} className="border border-black/5 bg-[#FAFBFC] p-2" data-testid={`item-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5 flex items-center gap-1.5">
                      <Input value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} placeholder="Item name" className="rounded-none h-9 flex-1" />
                      {it.brochure_path && <span title={it.brochure_filename || "Brochure attached"} className="text-[#D90429]" data-testid={`item-brochure-indicator-${idx}`}>📎</span>}
                    </div>
                    <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} placeholder="Qty" className="col-span-2 rounded-none h-9" />
                    <Input value={it.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)} placeholder="Unit" className="col-span-2 rounded-none h-9" />
                    <Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} placeholder="Price" className="col-span-2 rounded-none h-9" />
                    <button onClick={() => removeItem(idx)} className="col-span-1 p-2 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`remove-item-${idx}`}><Trash size={14} /></button>
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-xs text-[#4A4D57] cursor-pointer select-none" data-testid={`brochure-toggle-${idx}`}>
                    <input
                      type="checkbox"
                      checked={includeBrochure}
                      onChange={(e) => {
                        updateItem(idx, "include_brochure", e.target.checked);
                        if (e.target.checked && !it.brochure_path) {
                          toast.message(`No brochure on file for "${it.name || 'this item'}" — will skip on PDF.`);
                        }
                      }}
                      className="accent-[#D90429] w-4 h-4"
                    />
                    Attach product brochure (if available)
                    {it.brochure_path
                      ? <span className="text-[10px] font-mono-shelv text-[#0D9488] uppercase">on file: {it.brochure_filename || "brochure.pdf"}</span>
                      : <span className="text-[10px] font-mono-shelv text-[#717680] uppercase">no brochure on file</span>}
                  </label>
                </div>
              );})}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div><Label>Discount (₹)</Label><Input type="number" value={form.discount} onChange={(e) => setField("discount", e.target.value)} className="rounded-none" /></div>
              <div><Label>Tax %</Label><Input type="number" value={form.tax_percent} onChange={(e) => setField("tax_percent", e.target.value)} className="rounded-none" /></div>
            </div>
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="lg:col-span-5">
          <div className="sticky top-24 bg-white border border-black/10" data-testid="proposal-preview">
            <div className="bg-[#D90429] text-white px-6 py-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-white/80">Proposal</div>
              <div className="font-display text-2xl font-black tracking-tighter mt-1">SHELVESTECH</div>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <h2 className="font-display text-2xl font-bold tracking-tight">{form.title || "Proposal title"}</h2>
              <div className="text-sm text-[#4A4D57] mt-1">{form.client_name || "Client Name"} {form.client_company && `· ${form.client_company}`}</div>

              {form.introduction && (
                <div className="mt-5">
                  <div className="overline">Introduction</div>
                  <p className="text-sm leading-relaxed mt-2 whitespace-pre-wrap">{form.introduction}</p>
                </div>
              )}
              {form.requirements && (
                <div className="mt-5">
                  <div className="overline">Requirements</div>
                  <p className="text-sm leading-relaxed mt-2 whitespace-pre-wrap">{form.requirements}</p>
                </div>
              )}
              {form.scope && (
                <div className="mt-5">
                  <div className="overline">Scope</div>
                  <p className="text-sm leading-relaxed mt-2 whitespace-pre-wrap">{form.scope}</p>
                </div>
              )}
              {form.items.length > 0 && (
                <div className="mt-5">
                  <div className="overline">Commercials</div>
                  <table className="w-full text-xs mt-2">
                    <thead><tr className="bg-[#D90429] text-white"><th className="px-2 py-1.5 text-left">Item</th><th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Price</th><th className="px-2 py-1.5 text-right">Amount</th></tr></thead>
                    <tbody>
                      {form.items.map((it, i) => (
                        <tr key={it._uid || `summary_${i}`} className="border-b border-black/5"><td className="px-2 py-1.5">{it.name}</td><td className="px-2 py-1.5 text-right">{it.quantity} {it.unit}</td><td className="px-2 py-1.5 text-right font-mono-shelv">₹{Number(it.unit_price).toLocaleString("en-IN")}</td><td className="px-2 py-1.5 text-right font-mono-shelv">₹{(Number(it.quantity) * Number(it.unit_price)).toLocaleString("en-IN")}</td></tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan={3} className="px-2 py-1 text-right text-[#717680]">Subtotal</td><td className="px-2 py-1 text-right font-mono-shelv">₹{subtotal.toLocaleString("en-IN")}</td></tr>
                      {Number(form.discount) > 0 && <tr><td colSpan={3} className="px-2 py-1 text-right text-[#717680]">Discount</td><td className="px-2 py-1 text-right font-mono-shelv">-₹{Number(form.discount).toLocaleString("en-IN")}</td></tr>}
                      <tr><td colSpan={3} className="px-2 py-1 text-right text-[#717680]">Tax ({form.tax_percent}%)</td><td className="px-2 py-1 text-right font-mono-shelv">₹{tax.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td></tr>
                      <tr className="bg-[#FFE5E9] font-bold"><td colSpan={3} className="px-2 py-2 text-right">GRAND TOTAL</td><td className="px-2 py-2 text-right font-mono-shelv text-[#D90429]" data-testid="grand-total">₹{total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td></tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {form.terms && (
                <div className="mt-5">
                  <div className="overline">Terms</div>
                  <p className="text-sm leading-relaxed mt-2 whitespace-pre-wrap">{form.terms}</p>
                </div>
              )}
              {form.items.some((it) => it.brochure_path) && (
                <div className="mt-5">
                  <div className="overline">Attachments</div>
                  <ul className="mt-2 space-y-1.5">
                    {form.items.filter((it) => it.brochure_path).map((it) => (
                      <li key={it.brochure_path} className="text-xs flex items-center gap-2 text-[#0A0A0A]">
                        <span className="text-[#D90429]">📎</span> {it.brochure_filename || `${it.name} brochure`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Signature block (mirrors the PDF footer) */}
              <div className="mt-6 pt-4 border-t-2 border-[#D90429]">
                <div className="overline text-[#D90429]">Signed by</div>
                <div className="mt-2 text-xs leading-relaxed text-[#0A0A0A]">
                  Thanks &amp; Regards<br />
                  <span className="font-bold">Mukesh Panchal</span><br />
                  Shelves Tech Pvt. Ltd<br />
                  Office No -110-111, 1st Floor<br />
                  Gupta Arcade, Plot no-2<br />
                  Inder Enclave, LSC Rohtak Road<br />
                  Paschim Vihar New Delhi-110087<br />
                  Mobile-9899730901 / 8920474021<br />
                  GSTN-07AATCS6306H1Z0
                </div>
              </div>

              <div className="mt-4 text-[10px] text-[#717680] flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 bg-[#D90429]" />
                Generated PDFs include the Shelves Tech cover page on page 1.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
